/**
 * SessionTab — 분산 세션 (C036 · eraf-session) 라이브 데모
 * POST   /api/eraf/platform-lab/session/attributes
 * GET    /api/eraf/platform-lab/session
 * DELETE /api/eraf/platform-lab/session
 * GET    /api/eraf/platform-lab/session/redis-keys
 *
 * ★ 설계 대전제(spec §12.0): JWT stateless 인증과 이 데모 세션은 직교(무관).
 *   세션 무효화는 로그인(JWT)에 영향을 주지 않는다.
 *
 * 5상태:
 *   - default: 탭 진입, 세션 미조회 — 속성 저장 폼 + 안내 문구
 *   - loading: 저장/조회/무효화 호출 중 — 세션 카드 Spinner + 버튼 비활성
 *   - empty:   무효화 직후 또는 속성 0 — EmptyState
 *   - error:   Redis 미가용/네트워크 — ErrorState + [다시 시도]
 *   - dialog:  sessionId [전체 보기] 클릭 시 inline Callout(경고, 파괴적 confirm 없음)
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { EmptyState, ErrorState } from '@ui-kit';
import {
  useSaveSessionAttribute,
  useSessionInfo,
  useInvalidateSession,
  useSessionRedisKeys,
} from '../hooks/usePlatformLab';
import { sessionAttributeSchema } from '../schema/platformLab.schema';
import type { SessionAttributeFormValues } from '../schema/platformLab.schema';
import type { SessionInfoResponse, ApiError } from '../types/platformLab';

function formatInstant(iso: string): string {
  try {
    return new Date(iso).toLocaleString('ko-KR', { hour12: false });
  } catch {
    return iso;
  }
}

function formatMmSs(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}

/** JWT stateless 공존 설명 패널 — 상시 노출 (spec §12.0) */
function CoexistPanel() {
  const { t } = useTranslation();
  return (
    <div
      style={{
        marginTop: 'var(--spacing-4)',
        padding: 'var(--spacing-3) var(--spacing-4)',
        background: 'var(--color-surface-muted)',
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-md)',
        fontSize: 'var(--font-size-xs)',
        color: 'var(--color-text-secondary)',
      }}
    >
      <p style={{ margin: 0, marginBottom: 'var(--spacing-2)', fontWeight: 700, color: 'var(--color-text-primary)' }}>
        ⓘ {t('platformLab.session.coexist.title')}
      </p>
      <ul style={{ margin: 0, paddingLeft: 'var(--spacing-5)', lineHeight: 1.8 }}>
        <li>{t('platformLab.session.coexist.auth')}</li>
        <li>{t('platformLab.session.coexist.shared')}</li>
        <li>{t('platformLab.session.coexist.cookie')}</li>
        <li>{t('platformLab.session.coexist.logout')}</li>
      </ul>
    </div>
  );
}

export function SessionTab() {
  const { t } = useTranslation();
  const saveMutation = useSaveSessionAttribute();
  const loadMutation = useSessionInfo();
  const invalidateMutation = useInvalidateSession();
  const redisKeysQuery = useSessionRedisKeys();

  const [sessionInfo, setSessionInfo] = useState<SessionInfoResponse | null>(null);
  const [fetchedAt, setFetchedAt] = useState<number>(0);
  const [nowTick, setNowTick] = useState<number>(Date.now());
  const [everLoaded, setEverLoaded] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [revealWarningShown, setRevealWarningShown] = useState(false);

  const form = useForm<SessionAttributeFormValues>({
    resolver: zodResolver(sessionAttributeSchema),
    defaultValues: { key: '', value: '' },
  });

  // TTL 카운트다운 — 1s 간격
  useEffect(() => {
    if (!sessionInfo) return;
    const timer = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [sessionInfo]);

  const isPending = saveMutation.isPending || loadMutation.isPending || invalidateMutation.isPending;

  const applySessionInfo = (data: SessionInfoResponse) => {
    setSessionInfo(data);
    setFetchedAt(Date.now());
    setNowTick(Date.now());
    setEverLoaded(true);
    setError(null);
    redisKeysQuery.refetch();
  };

  const onSave = form.handleSubmit((values) => {
    setError(null);
    saveMutation.mutate(
      { key: values.key, value: values.value },
      {
        onSuccess: (data) => {
          applySessionInfo(data);
          form.reset({ key: '', value: '' });
        },
        onError: (err) => setError(err as unknown as ApiError),
      }
    );
  });

  const onLoad = () => {
    setError(null);
    loadMutation.mutate(undefined, {
      onSuccess: (data) => applySessionInfo(data),
      onError: (err) => setError(err as unknown as ApiError),
    });
  };

  const onInvalidate = () => {
    setError(null);
    invalidateMutation.mutate(undefined, {
      onSuccess: () => {
        setSessionInfo(null);
        setEverLoaded(true);
        setRevealWarningShown(false);
      },
      onError: (err) => setError(err as unknown as ApiError),
    });
  };

  const elapsedSinceFetch = sessionInfo ? Math.floor((nowTick - fetchedAt) / 1000) : 0;
  const remainingTtl = sessionInfo ? Math.max(0, sessionInfo.remainingTtlSeconds - elapsedSinceFetch) : 0;
  const attributeEntries = sessionInfo ? Object.entries(sessionInfo.attributes ?? {}) : [];

  const isEmpty = everLoaded && (!sessionInfo || attributeEntries.length === 0) && !isPending && !error;
  const showDefaultHint = !everLoaded && !sessionInfo && !isPending && !error;

  return (
    <div>
      <h2 style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, marginBottom: 'var(--spacing-4)', color: 'var(--color-text-primary)' }}>
        {t('platformLab.session.title')}
      </h2>

      {/* 속성 저장 폼 */}
      <form
        onSubmit={(e) => e.preventDefault()}
        style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-3)', alignItems: 'flex-end', marginBottom: 'var(--spacing-4)' }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
          {t('platformLab.session.input.key')}
          <input
            {...form.register('key')}
            type="text"
            aria-label={t('platformLab.session.input.key')}
            aria-invalid={!!form.formState.errors.key}
            style={{
              padding: 'var(--spacing-2) var(--spacing-3)',
              border: `1px solid ${form.formState.errors.key ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
              width: 180,
              fontFamily: 'var(--font-family-sans)',
            }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', flex: '1 1 200px' }}>
          {t('platformLab.session.input.value')}
          <input
            {...form.register('value')}
            type="text"
            aria-label={t('platformLab.session.input.value')}
            aria-invalid={!!form.formState.errors.value}
            style={{
              padding: 'var(--spacing-2) var(--spacing-3)',
              border: `1px solid ${form.formState.errors.value ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
              fontFamily: 'var(--font-family-sans)',
            }}
          />
        </label>
        <button
          type="button"
          onClick={onSave}
          disabled={isPending}
          style={{
            padding: 'var(--spacing-2) var(--spacing-4)',
            background: 'var(--color-brand-primary)',
            color: 'var(--color-text-inverse)',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 600,
            cursor: isPending ? 'not-allowed' : 'pointer',
            fontFamily: 'var(--font-family-sans)',
            opacity: isPending ? 0.6 : 1,
          }}
        >
          {saveMutation.isPending ? '...' : t('platformLab.session.button.save')}
        </button>
        <button
          type="button"
          onClick={onLoad}
          disabled={isPending}
          style={{
            padding: 'var(--spacing-2) var(--spacing-4)',
            background: 'var(--color-surface-elevated)',
            color: 'var(--color-text-primary)',
            border: '1px solid var(--color-surface-border)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 600,
            cursor: isPending ? 'not-allowed' : 'pointer',
            fontFamily: 'var(--font-family-sans)',
            opacity: isPending ? 0.6 : 1,
          }}
        >
          {loadMutation.isPending ? '...' : t('platformLab.session.button.load')}
        </button>
      </form>

      {/* loading */}
      {isPending && (
        <div aria-busy="true" style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-3)', color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
          <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: '50%', border: '2px solid var(--color-brand-primary)', borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
          {t('platformLab.session.button.load')}...
        </div>
      )}

      {/* default 안내 (아직 세션 조회 전) */}
      {showDefaultHint && (
        <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontStyle: 'italic', marginBottom: 'var(--spacing-3)' }}>
          {t('platformLab.session.default.hint')}
        </p>
      )}

      {/* error */}
      {error && !isPending && (
        <div style={{ marginBottom: 'var(--spacing-3)' }}>
          <ErrorState
            title={t('platformLab.session.error.redis')}
            message={error.message}
            onRetry={() => setError(null)}
          />
        </div>
      )}

      {/* empty — 무효화 직후 또는 속성 0 */}
      {isEmpty && (
        <div style={{ marginBottom: 'var(--spacing-3)' }}>
          <EmptyState title={t('platformLab.session.empty.title')} />
        </div>
      )}

      {/* 현재 세션 카드 */}
      {sessionInfo && attributeEntries.length > 0 && !isPending && !error && (
        <>
          <div
            style={{
              padding: 'var(--spacing-4)',
              background: 'var(--color-surface-elevated)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              marginBottom: 'var(--spacing-3)',
            }}
          >
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-2)', alignItems: 'center', marginBottom: 'var(--spacing-2)' }}>
              <strong style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
                {t('platformLab.session.label.sessionId')}:
              </strong>
              <code style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-primary)' }}>
                {sessionInfo.sessionIdMasked}
              </code>
              <button
                type="button"
                onClick={() => setRevealWarningShown(true)}
                style={{
                  background: 'transparent',
                  border: '1px solid var(--color-surface-border)',
                  borderRadius: 'var(--radius-sm)',
                  padding: 'var(--spacing-1) var(--spacing-2)',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-brand-primary)',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-family-sans)',
                }}
              >
                {t('platformLab.session.button.reveal')}
              </button>
              {sessionInfo.newlyCreated && (
                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-brand-primary)', fontWeight: 600 }}>
                  ({t('platformLab.session.newlyCreated')})
                </span>
              )}
            </div>

            {/* dialog — sessionId 전체 보기 경고 Callout */}
            {revealWarningShown && (
              <div
                role="status"
                aria-live="polite"
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 'var(--spacing-2)',
                  padding: 'var(--spacing-3)',
                  background: 'var(--color-warning-subtle, #fff9e6)',
                  border: '1px solid var(--color-warning, #f0ad4e)',
                  borderRadius: 'var(--radius-md)',
                  marginBottom: 'var(--spacing-3)',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-primary)',
                }}
              >
                <span>⚠</span>
                <div style={{ flex: 1 }}>{t('platformLab.session.reveal.warn')}</div>
                <button
                  type="button"
                  onClick={() => setRevealWarningShown(false)}
                  aria-label="닫기"
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', padding: 0 }}
                >
                  ×
                </button>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'var(--spacing-2)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-2)' }}>
              <div>
                <strong>{t('platformLab.session.label.created')}:</strong> {formatInstant(sessionInfo.creationTime)}
              </div>
              <div>
                <strong>{t('platformLab.session.label.accessed')}:</strong> {formatInstant(sessionInfo.lastAccessedTime)}
              </div>
              <div>
                <strong>{t('platformLab.session.label.ttl')}:</strong>{' '}
                {formatMmSs(remainingTtl)} / {formatMmSs(sessionInfo.maxInactiveIntervalSeconds)}
              </div>
            </div>

            {/* TTL 진행바 */}
            <div
              role="progressbar"
              aria-valuenow={remainingTtl}
              aria-valuemin={0}
              aria-valuemax={sessionInfo.maxInactiveIntervalSeconds}
              style={{
                width: '100%',
                height: 6,
                background: 'var(--color-surface-muted)',
                borderRadius: 'var(--radius-sm)',
                overflow: 'hidden',
                marginBottom: 'var(--spacing-3)',
              }}
            >
              <div
                style={{
                  width: `${sessionInfo.maxInactiveIntervalSeconds > 0 ? Math.min(100, (remainingTtl / sessionInfo.maxInactiveIntervalSeconds) * 100) : 0}%`,
                  height: '100%',
                  background: remainingTtl < 30 ? 'var(--color-danger)' : 'var(--color-brand-primary)',
                  transition: 'width 1s linear',
                }}
              />
            </div>

            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
              <strong>{t('platformLab.session.label.attrs')}:</strong>
              <ul style={{ margin: 'var(--spacing-1) 0 0', paddingLeft: 'var(--spacing-5)' }}>
                {attributeEntries.map(([k, v]) => (
                  <li key={k} style={{ fontFamily: 'var(--font-family-mono)' }}>
                    {k} = {v}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)', marginBottom: 'var(--spacing-3)', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={onInvalidate}
              disabled={isPending}
              style={{
                padding: 'var(--spacing-2) var(--spacing-4)',
                background: 'var(--color-danger)',
                color: 'var(--color-text-inverse)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                cursor: isPending ? 'not-allowed' : 'pointer',
                fontFamily: 'var(--font-family-sans)',
                opacity: isPending ? 0.6 : 1,
              }}
            >
              {invalidateMutation.isPending ? '...' : t('platformLab.session.button.invalidate')}
            </button>
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
              {t('platformLab.session.label.redisKeys')}: {redisKeysQuery.data?.keyCount ?? '-'}
            </span>
          </div>
        </>
      )}

      {/* JWT stateless 공존 설명 패널 — 상시 노출 */}
      <CoexistPanel />

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
